import { Button, Input } from 'ui';
import { Form } from 'antd';
import { useCartState } from '../../../context/CartContext';
import { CouponDiscounts } from '../../../../interfaces/Coupon';
import { useTranslations } from '../../../../admin/pages/Coupons/hooks/use-translations';
import { useCoupons } from './useCoupons';
import {
  sortCoupons,
  calculateFarmCouponDiscount,
} from 'utils/productTotalPriceHelper';

interface CouponsWithDiscounts extends CouponDiscounts {
  totalDiscount: number;
}

interface CouponsWithDiscountsMapper {
  couponsWithDiscounts: CouponsWithDiscounts[];
  farmTotalsWithCoupon: Map<number, number>;
}

export default function CouponManager() {
  const t = useTranslations();
  const { farmProducts } = useCartState();
  const { code, setCode, applyCoupon, removeCoupon, coupons, validating } =
    useCoupons();

  const couponsWithDiscounts = coupons
    .sort(sortCoupons)
    .reduce<CouponsWithDiscountsMapper>(
      (acc, c) => {
        const farm = farmProducts.find((farm) => farm.farm_id === c.farm_id);
        if (!farm) {
          return acc;
        }

        const totalWithCoupon =
          acc.farmTotalsWithCoupon.get(farm.farm_id) ??
          farm.farmPriceTotals.totalWithDiscount;

        let couponDiscount = 0;

        if (c.apply_to_whole_cart) {
          couponDiscount = calculateFarmCouponDiscount(
            c,
            farm.farm_id,
            totalWithCoupon
          );
        } else {
          couponDiscount = farm.products.reduce((sum, prod) => {
            if (!prod.cartProduct) {
              return sum;
            }

            const cartProductAmount = prod?.cartProduct?.product_amounts?.find(
              (amount) => amount.type === prod.cartType
            );

            const productId = prod.cartProduct.id;

            if (
              cartProductAmount?.storage_wholesale_limit !== 0 &&
              cartProductAmount?.storage_wholesale_limit !== null &&
              cartProductAmount?.storage_wholesale_limit !== undefined &&
              (cartProductAmount?.storage_wholesale_limit ?? 0) <=
                (prod.amount ?? 0)
            ) {
              return sum;
            }

            return sum + (c.productDiscounts?.[productId] ?? 0) * prod.amount;
          }, 0);
        }

        if (couponDiscount > 0) {
          acc.couponsWithDiscounts.push({
            ...c,
            totalDiscount: couponDiscount,
          });

          acc.farmTotalsWithCoupon.set(
            farm.farm_id,
            totalWithCoupon - couponDiscount
          );
        }

        return acc;
      },
      {
        couponsWithDiscounts: [],
        farmTotalsWithCoupon: new Map(),
      }
    ).couponsWithDiscounts;

  return (
    <div className="cart-farm-order-summary coupon">
      <Form.Item label={t('cart_summary.coupon.label')}>
        <Input
          className="coupon-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button
          className={'ant-btn ant-btn-primary coupon-btn'}
          onClick={applyCoupon}
          loading={validating}
          disabled={validating}
          label={t('cart_summary.coupon.apply')}
        />
      </Form.Item>

      {couponsWithDiscounts.length > 0 && (
        <div className="applied-coupons">
          {couponsWithDiscounts.map((c) => (
            <div key={'coupon_code_' + c.code} className="applied-coupon">
              <div>
                <div>
                  {t('cart_summary.coupon.title')}:
                  <br />
                  <span className="applied-coupon-small">
                    {c.code}&nbsp; ({c.discount_value ?? 0}
                    {t(`cart_summary.coupon.${c.discount_type}`)})
                  </span>
                </div>

                <b>{c.totalDiscount.toFixed(2)} €</b>
              </div>
              <Button
                className={'ant-btn ant-btn-primary coupon-btn'}
                onClick={() => removeCoupon(c.code)}
                loading={validating}
                disabled={validating}
                label={t('cart_summary.coupon.remove')}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
